import React, { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";

export default function AIAssistant({ onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello — I'm the CBC Loyalty Assistant, powered by ChannelLoyalty.ai. Ask me about wallets, milestones, referrals, campaigns, or next-best-actions. — CBC Assistant · ChannelLoyalty.ai" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${formatError(e)}` }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-50 flex items-end md:items-center justify-end p-0 md:p-6" data-testid="ai-assistant-modal">
      <div className="w-full md:w-[440px] h-[80vh] md:h-[640px] bg-white border border-slate-200 rounded-t-2xl md:rounded-2xl shadow-xl flex flex-col overflow-hidden">
        <div className="bg-brand-navy text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-teal-700 flex items-center justify-center">
              <Sparkles size={16} />
            </div>
            <div className="leading-tight">
              <div className="text-[13px] font-medium">CBC Loyalty Assistant</div>
              <div className="text-[10px] text-teal-300 small-caps">Powered by ChannelLoyalty.ai</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="close-ai-assistant"><X size={18} /></button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] text-[13px] leading-relaxed px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-brand-navy text-white rounded-tr-sm" : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 text-[12px] text-slate-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-3 bg-white flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about points, milestones, campaigns…"
            className="flex-1"
            data-testid="ai-assistant-input"
          />
          <button onClick={send} disabled={sending} className="btn-teal flex items-center gap-2" data-testid="ai-assistant-send">
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
